import {useCallback, useState} from 'react';

import {EdgePolicyResultPolicyPeriodStatus, EdgeSubmissionResult} from '@amicamutual/amica-common';
import {Formik, FormikProps} from 'formik';
import {isEmpty} from 'ramda';
import {useMediaQuery} from 'react-responsive';
import {useLocation, useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import * as yup from 'yup';

import autoIDCard from 'assets/images/create-account-auto-id-card.svg';
import billingStatement from 'assets/images/create-account-billing-statement.svg';
import email from 'assets/images/create-account-email.svg';
import lifeBillingStatement from 'assets/images/create-account-life-billing-statement.svg';
import policyDeclarations from 'assets/images/create-account-policy-declarations.svg';
import {Flex} from 'components/base/Flex';
import {ActionStyle, ActionType, BodyText, BodyTypes, ButtonStyle, ButtonWidth, H2} from 'components/design-guide';
import Action from 'components/design-guide/Action';
import {getRoute} from 'config/navigation';
import {Placeholder} from 'enums/input';
import {AccountAlreadyExists} from 'features/CreateAccount/components/AccountAlreadyExists';
import CreateAccountErrorBanner, {isSystemError} from 'features/CreateAccount/components/CreateAccountErrorBanner';
import {CreateAccountProvisionErrors} from 'features/CreateAccount/enums';
import {useNavigateToCreateAccountRoutes} from 'features/CreateAccount/hooks/navigationState';
import {useProvisionAccountMutation} from 'features/CreateAccount/hooks/queries';
import {CREATE_ACCOUNT_TRANSLATIONS} from 'features/CreateAccount/translations';
import {CREATE_ACCOUNT_QUESTIONS_TRANSLATIONS} from 'features/CreateAccount/translations/CreateAccountQuestions';
import {ProvisioningError} from 'features/CreateAccount/types/error';
import {hasValidationOption} from 'features/CreateAccount/utils';
import FloatingLabelInput from 'shared/components/FormComponents/FloatingLabelInput';
import MessageBanner from 'shared/components/MessageBanner';
import {UnauthenticatedPage} from 'shared/components/Unauthenticated';
import {ensightenPageTags, pageDataTemplates} from 'shared/Ensighten/utils';
import FormattedMessage from 'shared/IntlFormatMessage/components/FormattedMessage';
import {GLOBAL_TRANSLATIONS, GLOBAL_VALIDATIONS} from 'shared/IntlFormatMessage/translations/globalTranslations';
import {intlFormatSingleMessage} from 'shared/IntlFormatMessage/utils/intlMesssageFormat';
import {AccountPageTitles} from 'shared/PageHelmet/enums/PageTitles';
import {Spacing, Breakpoints} from 'styles/Base';
import {SESSION_STORAGE_NAMESPACE_QUOTING} from 'types/SessionNamespace';
import {isValidBirthDate} from 'utils/birth-date';
import {dateToDateOnly} from 'utils/date-time';
import {regex, isPolicyOrAccountNumber, Mask} from 'utils/regex';

const FormWrapper = styled.div`
  width: 65%;

  ${`@media (${Breakpoints.MOBILE})`} {
    width: 100%;
  }
`;

const PageActionButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: ${Spacing.TWO};
  margin-bottom: ${Spacing.TWO};
  justify-content: space-between;

  ${`@media (${Breakpoints.MOBILE})`} {
    flex-direction: column-reverse;
  }
`;

const pageData = {
  ...pageDataTemplates.createAccount,
  name: ensightenPageTags.CREATE_ACCOUNT_QUESTIONS
};

interface FormValues {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  policyOrAccountNumber: string;
}

const CreateAccountQuestions = () => {
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const {navigateToConfirmation, navigateToVerification} = useNavigateToCreateAccountRoutes();
  const isMobileScreen = useMediaQuery({query: `(${Breakpoints.MOBILE})`});
  const {
    mutate: provisionAccount,
    isPending: isProvisioningAccount,
    error: provisionError
  } = useProvisionAccountMutation();

  const [showExpiredMessage, setShowExpiredMessage] = useState(pathname === getRoute('/create-account/expired'));
  const [accountExists, setAccountExists] = useState(false);

  const quotingObjectFromSession = sessionStorage.getItem(SESSION_STORAGE_NAMESPACE_QUOTING);
  const parseSessionObject = quotingObjectFromSession ? JSON.parse(quotingObjectFromSession)?.quoting : undefined;

  const workorder: EdgeSubmissionResult | undefined =
    parseSessionObject?.workOrders[parseSessionObject?.quoteType.currentQuoteType];
  const accountHolder =
    workorder && !isEmpty(workorder.baseData.accountHolders)
      ? workorder.baseData.accountHolders?.find(({accountHolderType}) => accountHolderType === 'PRIMARY')
      : undefined;

  const isVerbalBound = workorder?.policyPeriodStatus === EdgePolicyResultPolicyPeriodStatus.VERBALBOUND;

  const onCancel = () => {
    navigate(getRoute('/login'));
  };

  const hideAccountExists = () => setAccountExists(false);

  const initialValues: FormValues = {
    firstName: accountHolder?.firstName ?? '',
    lastName: accountHolder?.lastName ?? '',
    dateOfBirth: '',
    policyOrAccountNumber: workorder?.bindData?.accountNumber ?? ''
  };

  const onSubmit = useCallback(
    (values: FormValues) => {
      setShowExpiredMessage(false);
      const firstName = values.firstName;
      const lastName = values.lastName;
      const dateOfBirth = dateToDateOnly(new Date(values.dateOfBirth));
      const policyOrAccountNumber = values.policyOrAccountNumber;
      if (!isProvisioningAccount) {
        provisionAccount(
          {firstName, lastName, dateOfBirth, scsKey: policyOrAccountNumber},
          {
            onSuccess: (response) => navigateToConfirmation({activationEmailAddress: response.activationEmailAddress}),
            onError: ({errorCode, sessionId, validationOptions}: ProvisioningError) => {
              if (
                errorCode === CreateAccountProvisionErrors.CONFLICT &&
                sessionId &&
                hasValidationOption(validationOptions)
              ) {
                navigateToVerification({validationOptions, sessionId});
              } else if (errorCode === CreateAccountProvisionErrors.ALREADY_EXISTS) {
                setAccountExists(true);
              }
            }
          }
        );
      }
    },
    [isProvisioningAccount, navigateToConfirmation, navigateToVerification, provisionAccount]
  );

  const labels = {
    firstName: intlFormatSingleMessage(GLOBAL_TRANSLATIONS.FIRST_NAME),
    lastName: intlFormatSingleMessage(GLOBAL_TRANSLATIONS.LAST_NAME),
    dateOfBirth: intlFormatSingleMessage(GLOBAL_TRANSLATIONS.DATE_OF_BIRTH),
    policyOrAccountNumber: intlFormatSingleMessage(CREATE_ACCOUNT_QUESTIONS_TRANSLATIONS.POLICY_OR_ACCOUNT_NUMBER)
  };

  const requiredErrors = {
    firstName: intlFormatSingleMessage(GLOBAL_VALIDATIONS.VALID_FIRST_NAME),
    lastName: intlFormatSingleMessage(GLOBAL_VALIDATIONS.VALID_LAST_NAME),
    dateOfBirth: intlFormatSingleMessage(GLOBAL_VALIDATIONS.VALID_DOB),
    policyOrAccountNumber: intlFormatSingleMessage(
      CREATE_ACCOUNT_QUESTIONS_TRANSLATIONS.POLICY_OR_ACCOUNT_NUMBER_VALIDATION
    )
  };

  const validationSchema = yup.object().shape({
    firstName: yup.string().required(requiredErrors.firstName).matches(regex.CUSTOMER_NAME, requiredErrors.firstName),
    lastName: yup.string().required(requiredErrors.lastName).matches(regex.CUSTOMER_NAME, requiredErrors.lastName),
    dateOfBirth: yup
      .string()
      .required(requiredErrors.dateOfBirth)
      .min(10, requiredErrors.dateOfBirth)
      .test('is-valid-date-of-birth', requiredErrors.dateOfBirth, (value: string) => isValidBirthDate(value)),
    policyOrAccountNumber: yup
      .string()
      .required(requiredErrors.policyOrAccountNumber)
      .min(2, requiredErrors.policyOrAccountNumber)
      .test('is-valid-policy-or-account-number', requiredErrors.policyOrAccountNumber, (value: string) =>
        isPolicyOrAccountNumber(value)
      )
  });

  const [isOpen, setIsOpen] = useState(false);

  const handleOpenInfo = () => {
    setIsOpen(!isOpen);
  };

  const alreadyExists = provisionError?.errorCode === CreateAccountProvisionErrors.ALREADY_EXISTS;
  const systemError =
    !alreadyExists &&
    ((provisionError?.errorCode === CreateAccountProvisionErrors.CONFLICT &&
      (!provisionError.validationOptions.length || !provisionError.sessionId)) ||
      isSystemError(provisionError?.errorCode));

  return (
    <UnauthenticatedPage
      {...{pageData}}
      title={intlFormatSingleMessage(CREATE_ACCOUNT_TRANSLATIONS.CREATE_YOUR_ONLINE_ACCOUNT)}
      bannerFullWidth
      banner={systemError && <CreateAccountErrorBanner error={provisionError?.errorCode} />}
      pageHelmetTitle={AccountPageTitles.ACCOUNT_INFORMATION}>
      <H2>{intlFormatSingleMessage(CREATE_ACCOUNT_QUESTIONS_TRANSLATIONS.PERSONAL_INFORMATION_SUBHEADING)}</H2>
      <BodyText type={BodyTypes.BODYP} marginBottom={Spacing.ZERO_5}>
        {intlFormatSingleMessage(CREATE_ACCOUNT_QUESTIONS_TRANSLATIONS.PERSONAL_INFORMATION_DESCRIPTION)}
      </BodyText>
      <BodyText type={BodyTypes.SMALLBODYP} marginTop={Spacing.ZERO} marginBottom={Spacing.ONE}>
        {intlFormatSingleMessage(CREATE_ACCOUNT_QUESTIONS_TRANSLATIONS.NAME_SAME_AS_LICENSE)}
      </BodyText>
      {provisionError?.errorCode && !systemError && !alreadyExists && (
        <CreateAccountErrorBanner error={provisionError?.errorCode} />
      )}
      {showExpiredMessage && (
        <MessageBanner
          type="error"
          heading={intlFormatSingleMessage(CREATE_ACCOUNT_QUESTIONS_TRANSLATIONS.ACTIVATION_EXPIRED_TITLE)}
          subtext={intlFormatSingleMessage(CREATE_ACCOUNT_QUESTIONS_TRANSLATIONS.ACTIVATION_EXPIRED_MESSAGE)}
          contextual
          dismissable
        />
      )}
      {<AccountAlreadyExists isOpen={accountExists} onClose={hideAccountExists} />}
      <FormWrapper>
        <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
          {({errors, handleChange, handleSubmit, touched, values}: FormikProps<FormValues>) => (
            <form onSubmit={handleSubmit}>
              <FloatingLabelInput
                name="create-account-first-name"
                value={values.firstName}
                inputLabelText={labels.firstName}
                maxLength={50}
                errorText={touched.firstName && errors.firstName ? errors.firstName : ''}
                onChange={handleChange('firstName')}
                aria-required
                disabled={isVerbalBound}
              />
              <FloatingLabelInput
                name="create-account-last-name"
                value={values.lastName}
                inputLabelText={labels.lastName}
                maxLength={50}
                errorText={touched.lastName && errors.lastName ? errors.lastName : ''}
                onChange={handleChange('lastName')}
                aria-required
                disabled={isVerbalBound}
                hintText={intlFormatSingleMessage(CREATE_ACCOUNT_QUESTIONS_TRANSLATIONS.LAST_NAME_HELPER_TEXT)}
              />
              <FloatingLabelInput
                name="create-account-dob"
                type="text"
                value={values.dateOfBirth}
                mask={Mask.DATE}
                inputLabelText={labels.dateOfBirth}
                hintText={Placeholder.DATE}
                errorText={touched.dateOfBirth && errors.dateOfBirth ? errors.dateOfBirth : ''}
                onChange={handleChange('dateOfBirth')}
                aria-required
              />
              <FloatingLabelInput
                name="create-account-policy-or-account-number"
                value={values.policyOrAccountNumber}
                inputLabelText={labels.policyOrAccountNumber}
                maxLength={10}
                onChange={handleChange('policyOrAccountNumber')}
                errorText={
                  touched.policyOrAccountNumber && errors.policyOrAccountNumber ? errors.policyOrAccountNumber : ''
                }
                aria-required
                disabled={isVerbalBound}
              />

              <Flex flexDirection="column">
                <Action
                  name="find-my-info"
                  actionType={ActionType.ONCLICK}
                  actionStyle={ActionStyle.TEXT}
                  typeProps={{type: 'button', onClick: handleOpenInfo}}
                  css={{textAlign: 'right'}}>
                  <FormattedMessage message={CREATE_ACCOUNT_QUESTIONS_TRANSLATIONS.WHERE_CAN_I_FIND_THIS_INFORMATION} />
                </Action>
              </Flex>

              {isOpen && (
                <Flex flexDirection="column">
                  <BodyText type={BodyTypes.BODYP}>
                    <FormattedMessage message={CREATE_ACCOUNT_QUESTIONS_TRANSLATIONS.LOCATE_ACCOUNT_NUMBER_IN_EMAIL} />
                  </BodyText>
                  <img
                    src={email}
                    alt={intlFormatSingleMessage(CREATE_ACCOUNT_QUESTIONS_TRANSLATIONS.LOCATE_ACCOUNT_NUMBER_IN_EMAIL)}
                  />
                  <BodyText type={BodyTypes.BODYP}>
                    <FormattedMessage
                      message={CREATE_ACCOUNT_QUESTIONS_TRANSLATIONS.LOCATE_ACCOUNT_POLICY_DECLARATIONS}
                    />
                  </BodyText>
                  <img
                    src={policyDeclarations}
                    alt={intlFormatSingleMessage(
                      CREATE_ACCOUNT_QUESTIONS_TRANSLATIONS.LOCATE_ACCOUNT_POLICY_DECLARATIONS
                    )}
                  />
                  <BodyText type={BodyTypes.BODYP}>
                    <FormattedMessage message={CREATE_ACCOUNT_QUESTIONS_TRANSLATIONS.LOCATE_ACCOUNT_ID_CARD} />
                  </BodyText>
                  <img
                    src={autoIDCard}
                    alt={intlFormatSingleMessage(CREATE_ACCOUNT_QUESTIONS_TRANSLATIONS.LOCATE_ACCOUNT_ID_CARD)}
                  />
                  <BodyText type={BodyTypes.BODYP}>
                    <FormattedMessage
                      message={CREATE_ACCOUNT_QUESTIONS_TRANSLATIONS.LOCATE_ACCOUNT_BILLING_STATEMENT}
                    />
                  </BodyText>
                  <img
                    src={billingStatement}
                    alt={intlFormatSingleMessage(
                      CREATE_ACCOUNT_QUESTIONS_TRANSLATIONS.LOCATE_ACCOUNT_BILLING_STATEMENT
                    )}
                  />
                  <img
                    style={{marginTop: Spacing.ONE_5}}
                    src={lifeBillingStatement}
                    alt={intlFormatSingleMessage(
                      CREATE_ACCOUNT_QUESTIONS_TRANSLATIONS.LOCATE_ACCOUNT_BILLING_STATEMENT
                    )}
                  />
                </Flex>
              )}

              <PageActionButtonWrapper>
                <Action
                  name="create-account-cancel"
                  actionStyle={ActionStyle.BUTTON}
                  styleProps={{
                    buttonStyle: ButtonStyle.TERTIARY,
                    width: isMobileScreen ? ButtonWidth.FULL_AT_MOBILE : '185px'
                  }}
                  margin={{marginBottom: Spacing.ZERO_5}}
                  actionType={ActionType.ONCLICK}
                  typeProps={{type: 'button', onClick: onCancel}}>
                  <FormattedMessage message={GLOBAL_TRANSLATIONS.CANCEL} />
                </Action>
                <Action
                  name="create-account-submit"
                  actionStyle={ActionStyle.BUTTON}
                  styleProps={{
                    buttonStyle: ButtonStyle.PRIMARY,
                    width: isMobileScreen ? ButtonWidth.FULL_AT_MOBILE : isProvisioningAccount ? undefined : '185px',
                    withSpinner: {active: isProvisioningAccount}
                  }}
                  actionType={ActionType.ONCLICK}
                  typeProps={{type: 'submit'}}
                  disabled={isProvisioningAccount}
                  margin={{marginLeft: isMobileScreen ? undefined : Spacing.ONE, marginBottom: Spacing.ONE_25}}>
                  <FormattedMessage
                    message={isProvisioningAccount ? GLOBAL_TRANSLATIONS.SUBMITTING : GLOBAL_TRANSLATIONS.CONTINUE}
                  />
                </Action>
              </PageActionButtonWrapper>
            </form>
          )}
        </Formik>
      </FormWrapper>
    </UnauthenticatedPage>
  );
};

export default CreateAccountQuestions;
